import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api } from '../lib/api';
import { requireAccess } from '../lib/guards';
import { useSession } from '../lib/session';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';
import { SkeletonList } from '../components/ui/Skeleton';
import {
  auftraegeApi,
  type Auftrag,
  type BueroEntscheidungAktiv,
  type EntscheideDurchBueroInput,
} from '../lib/auftraege-api';
import {
  OffeneEntscheidungenFilterBar,
  type OffeneEntscheidungenFilter,
} from '../components/auftraege/OffeneEntscheidungenFilterBar';
import {
  OffeneEntscheidungenListe,
  type ZeilenAktion,
} from '../components/auftraege/OffeneEntscheidungenListe';
import {
  BulkEntscheidungDialog,
  type BulkAktion,
} from '../components/auftraege/BulkEntscheidungDialog';
import { StornoDialog } from '../components/auftraege/StornoDialog';

interface User {
  id: string;
  email: string;
  displayName: string;
}

export const Route = createFileRoute('/auftraege/offene-entscheidungen')({
  beforeLoad: () => requireAccess('/api/v1/auftraege/offene-entscheidungen'),
  component: OffeneEntscheidungenPage,
});

const DEFAULT_FILTER: OffeneEntscheidungenFilter = {
  klassifizierung: 'alle',
  assignedTo: 'alle',
  vonDatum: '',
  bisDatum: '',
};

function applyFilter(items: Auftrag[], filter: OffeneEntscheidungenFilter): Auftrag[] {
  return items.filter((item) => {
    if (filter.klassifizierung === 'alle') {
      // pass
    } else if (filter.klassifizierung === '') {
      if (item.maKlassifizierung !== null) return false;
    } else if (item.maKlassifizierung !== filter.klassifizierung) {
      return false;
    }
    if (filter.assignedTo !== 'alle' && item.assignedTo !== filter.assignedTo) {
      return false;
    }
    if (filter.vonDatum) {
      const von = new Date(filter.vonDatum + 'T00:00:00').getTime();
      if (new Date(item.createdAt).getTime() < von) return false;
    }
    if (filter.bisDatum) {
      const bis = new Date(filter.bisDatum + 'T23:59:59').getTime();
      if (new Date(item.createdAt).getTime() > bis) return false;
    }
    return true;
  });
}

function OffeneEntscheidungenPage(): JSX.Element {
  const session = useSession();
  const qc = useQueryClient();
  const canEntscheiden = session.data?.role === 'admin' || session.data?.role === 'buero';

  const listeQ = useQuery({
    queryKey: ['auftraege', 'offene-entscheidungen'],
    queryFn: () => auftraegeApi.listOffeneEntscheidungen(),
  });
  const usersQ = useQuery({
    queryKey: ['users', 'lookup'],
    queryFn: () => api<{ items: User[] }>('/api/v1/users'),
  });

  const [filter, setFilter] = useState<OffeneEntscheidungenFilter>(DEFAULT_FILTER);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  // Storno-Dialog: zwischen Klick "Stornieren" und Grund-Eingabe.
  // single = ein Auftrag aus Zeilen-Aktion; bulk = N aus Bulk-Dialog.
  const [stornoDialogState, setStornoDialogState] = useState<
    { kind: 'single'; id: string } | { kind: 'bulk'; ids: string[] } | null
  >(null);

  const items = useMemo(() => listeQ.data?.items ?? [], [listeQ.data]);
  const filtered = useMemo(() => applyFilter(items, filter), [items, filter]);
  const filteredIds = useMemo(() => new Set(filtered.map((i) => i.id)), [filtered]);
  const visibleSelectedIds = useMemo(
    () => new Set([...selectedIds].filter((id) => filteredIds.has(id))),
    [selectedIds, filteredIds],
  );

  const assigneeOptions = useMemo(() => {
    const users = usersQ.data?.items ?? [];
    return users.map((u) => ({ id: u.id, label: u.displayName || u.email }));
  }, [usersQ.data]);

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['auftraege'] });
    qc.invalidateQueries({ queryKey: ['reklamationen'] });
  }

  const entscheidenM = useMutation({
    mutationFn: (input: { id: string; body: EntscheideDurchBueroInput }) =>
      auftraegeApi.entscheiden(input.id, input.body),
    onSuccess: () => invalidate(),
  });

  // FX-003: Storno via dediziertem Endpoint mit Pflicht-Grund. Pfad ist
  // hier immer 'storniert' — die Queue zeigt nur Aufträge in
  // bueroEntscheidung='pending', die per Konstruktion (selbst_erfasst →
  // Status='entwurf') noch nicht in 'in_arbeit' sind.
  const stornoM = useMutation({
    mutationFn: (input: { id: string; grund: string }) =>
      auftraegeApi.stornieren(input.id, { grund: input.grund, pfad: 'storniert' }),
    onSuccess: () => invalidate(),
  });

  function markBusy(id: string, busy: boolean) {
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  async function runZeilenAktion(id: string, aktion: ZeilenAktion) {
    if (aktion === 'stornieren') {
      // Storno: Dialog für Grund-Eingabe öffnen, kein direkter Mutate.
      setStornoDialogState({ kind: 'single', id });
      return;
    }
    markBusy(id, true);
    try {
      const entscheidung: BueroEntscheidungAktiv = aktion;
      await entscheidenM.mutateAsync({ id, body: { entscheidung } });
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } finally {
      markBusy(id, false);
    }
  }

  async function runBulk(aktion: BulkAktion) {
    const ids = [...visibleSelectedIds];
    if (ids.length === 0) return;
    setBulkOpen(false);
    if (aktion === 'stornieren') {
      // Bulk-Storno: Dialog für gemeinsamen Grund öffnen.
      setStornoDialogState({ kind: 'bulk', ids });
      return;
    }
    ids.forEach((id) => markBusy(id, true));
    const failed: string[] = [];
    await Promise.allSettled(
      ids.map(async (id) => {
        try {
          await entscheidenM.mutateAsync({ id, body: { entscheidung: aktion } });
        } catch {
          failed.push(id);
        }
      }),
    );
    ids.forEach((id) => markBusy(id, false));
    if (failed.length === 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(failed));
    }
  }

  async function confirmStorno(grund: string) {
    if (!stornoDialogState) return;
    const ids =
      stornoDialogState.kind === 'single' ? [stornoDialogState.id] : stornoDialogState.ids;
    setStornoDialogState(null);
    ids.forEach((id) => markBusy(id, true));
    const failed: string[] = [];
    await Promise.allSettled(
      ids.map(async (id) => {
        try {
          await stornoM.mutateAsync({ id, grund });
        } catch {
          failed.push(id);
        }
      }),
    );
    ids.forEach((id) => markBusy(id, false));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => {
        if (!failed.includes(id)) next.delete(id);
      });
      return next;
    });
  }

  function toggleSelect(id: string, selected: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function selectAllVisible(allSelected: boolean) {
    if (allSelected) {
      setSelectedIds((prev) => new Set([...prev, ...filteredIds]));
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filteredIds.forEach((id) => next.delete(id));
        return next;
      });
    }
  }

  if (!canEntscheiden) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Offene Entscheidungen"
          description="Selbst erfasste Aufträge, die auf eine kommerzielle Entscheidung warten."
        />
        <div className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900">
          Du hast keine Berechtigung, Aufträge kommerziell zu entscheiden.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Offene Entscheidungen"
        description="Selbst erfasste Aufträge, die auf eine kommerzielle Entscheidung warten."
      />

      {listeQ.error ? <ErrorBanner error={listeQ.error as Error} /> : null}

      <OffeneEntscheidungenFilterBar
        value={filter}
        onChange={setFilter}
        assigneeOptions={assigneeOptions}
      />

      {visibleSelectedIds.size > 0 && (
        <div className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
          <span className="text-slate-700">
            {visibleSelectedIds.size} {visibleSelectedIds.size === 1 ? 'Auftrag' : 'Aufträge'}{' '}
            ausgewählt
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setBulkOpen(true)}
              className="rounded border border-amber-600 bg-amber-600 px-3 py-1 text-white hover:bg-amber-700"
            >
              Auswahl entscheiden
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="rounded border border-slate-300 bg-white px-3 py-1 text-slate-700 hover:bg-slate-100"
            >
              Auswahl aufheben
            </button>
          </div>
        </div>
      )}

      {listeQ.isLoading ? (
        <SkeletonList />
      ) : (
        <OffeneEntscheidungenListe
          items={filtered}
          selectedIds={visibleSelectedIds}
          onToggleSelect={toggleSelect}
          onSelectAll={selectAllVisible}
          onSingleAktion={(id, aktion) => {
            void runZeilenAktion(id, aktion);
          }}
          busyIds={busyIds}
        />
      )}

      <BulkEntscheidungDialog
        open={bulkOpen}
        count={visibleSelectedIds.size}
        onConfirm={(aktion) => {
          void runBulk(aktion);
        }}
        onClose={() => setBulkOpen(false)}
        busy={entscheidenM.isPending || stornoM.isPending}
      />

      <StornoDialog
        open={stornoDialogState !== null}
        count={
          stornoDialogState?.kind === 'bulk'
            ? stornoDialogState.ids.length
            : stornoDialogState
              ? 1
              : 0
        }
        pfad="storniert"
        onConfirm={(grund) => {
          void confirmStorno(grund);
        }}
        onClose={() => setStornoDialogState(null)}
        busy={stornoM.isPending}
      />
    </div>
  );
}
